import type {
  WidgetConfig as GQLItem,
  WidgetConfigConnection as GQLConnection,
} from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';

/**
 * An embeddable ticket widget's configuration — one row of the admin Widgets screen.
 *
 * Unrelated to the `WidgetConfig` interface in `packages/widget`, which is the embeddable
 * widget's own runtime config.
 */
export type WidgetConfig = {
  id?: number;
  description?: string;
  loaderUrl?: string;
  /** Read-only: the widget screen builds its embed `<script>` tag from this. */
  embedUrl?: string;
  /**
   * The `JSON` scalar, which codegen types as `any`. The widget form binds it as text and
   * validates it with `JSON.parse`, so it is carried as a string and handed over untouched.
   */
  configuration?: string;
  style?: string;
};

/**
 * No `token`, though the wire type has one: the admin mapper this replaces left it out of its
 * `take` list and nothing reads it. Add it to the type, the fragment and this list together if a
 * screen ever needs it.
 */
export const fromGql = (item: GQLItem): WidgetConfig =>
  take<WidgetConfig>(item, 'id', 'description', 'loaderUrl', 'embedUrl', 'configuration', 'style');

/**
 * `edges` and its `node` are both nullable on the schema, so the shared `GqlConnection.fromGql` is
 * not usable directly. `nodes` is derived from the mapped edges rather than selected beside them —
 * selecting both would serialize every row twice.
 */
export const fromGqlConnection = (connection: GQLConnection): GqlConnection.GqlConnection<WidgetConfig> => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
    totalCount: connection.totalCount,
  };
};
